import { useCallback, useEffect, useState, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { useParams } from 'react-router-dom'

import { useAuthStore } from '@caradasstore/AuthStore'
import { AssignedUser } from '@components/assigned-user'
import { CarImageHolder } from '@components/car-image-holder'
import { CustomItem } from '@components/custom-item'
import { Drawers } from '@components/drawers'
import { EmailForm } from '@components/email'
import { IdDisplay } from '@components/id-display'
import { Note } from '@components/note'
import { VinInput } from '@components/vin'
import {
  AdasAlert,
  AdasBox,
  AdasButton,
  AdasContainer,
  AdasDivider,
  AdasModal,
  AdasPaper,
  AdasStep,
  AdasStepper,
  AdasTextField,
  AdasTypography,
  AdasImage
} from '@components/wrapper-components'
import CheckIcon from '@mui/icons-material/Check'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import {
  closeQuote,
  getCalibrationList,
  getQuote,
  getQuoteEmails,
  getQuotePdf,
  getQuoteByReferenceNumber,
  getServiceList,
  transformToWO,
  updateNote,
  updateQuote,
  updateWorkOrderCalibrations,
  updateWorkOrderServices,
  getQWIByQuoteId,
  updateClient,
  updateWorkOrderParts,
  updateWorkOrderLabour,
  getPartsList,
  getLabourList,
  getReferralByCode
} from '../../../api/api'
import { PARAM_NAMES, getWorkOrderSummaryUrl, REDIRECT_URLS, getUrlByNameAndId } from '../../../constants'
import { useLoadingStore } from '../../../store'
import { useScrollTop } from '../../../utils/useScrollTop'
import { currencyFormatter } from '../../../utils/utils'
import { StatusEnum } from 'enums/enums'
import { Logger } from '../../../logger'
import { DropdownActionButtons } from '@components/buttons'
import ExpandableSection from '@components/custom-item/item-list/ExpandableSection'
import { EditProductPricing } from '@components/product/edit-product-pricing/EditProductPricing'
import theme from 'theme/theme'
import { useEditPricing } from '../../../hooks/useEditPricing'
import { handleDrawerClose } from '../../../hooks/handleDrawerClose'
import { transformToMap, flattenItem, normalizeCustomItems, splitCustomItems } from '../../../utils/itemHelpers'
import { MAX_DESCRIPTION_LENGTH } from '../../../constants'
import { itemHandlersFactory } from 'utils/populateItemsUtil'
import { ProductContractBanner } from '@components/product'
export const QuoteSummaryPage = () => {
  useScrollTop()
  const { [PARAM_NAMES.REFERENCE_NUMBER]: referenceNumber } = useParams()
  const [packages, setPackages] = useState([])
  const [services, setServices] = useState([])
  const [parts, setParts] = useState([])
  const [labor, setLabor] = useState([])
  const [availableCalibrations, setAvailableCalibrations] = useState() // previously allCalibrations
  const [availableServices, setAvailableServices] = useState() // previously allServices
  const [availableParts, setAvailableParts] = useState() // previously allParts
  const [availableLabour, setAvailableLabour] = useState() // previously allLabour
  const [isCalibrationOpen, setIsCalibrationOpen] = useState(false)
  const [isServiceOpen, setIsServiceOpen] = useState(false)
  const [isPartsOpen, setIsPartsOpen] = useState(false)
  const [isLabourOpen, setIsLabourOpen] = useState(false)

  const setLoading = useLoadingStore((store) => store.setLoading)
  const authCtx = useAuthStore()
  const [quoteData, setQuoteData] = useState({})
  const [quoteInputValues, setQuoteInputValues] = useState({
    notes_external: quoteData.notes_external || '',
    notes_internal: quoteData.notes_internal || '',
    client_notes: quoteData.client?.notes || ''
  })

  const [closeInput, setCloseInput] = useState('')
  const [formData, setFormData] = useState({})
  const [isVinEdit, setIsVinEdit] = useState(false)
  const [isEmail, setIsEmail] = useState(false)
  const [emailContacts, setEmailContacts] = useState({})
  const [quotePdf, setQuotePdf] = useState()
  const [open, setOpen] = useState(false)
  const [updatedVehicle, setUpdatedVehicle] = useState()
  const [isVinInvalid, setIsVinInvalid] = useState(false)
  const [customItemList, setCustomItemList] = useState([])
  const [rebateCustomItemList, setRebateCustomItemList] = useState([])
  const [QWIData, setQWIData] = useState(undefined)
  const [referralData, setReferralData] = useState()
  const textDisplay = { textAlign: 'center' }
  const history = useHistory()
  const redirectVehicleInfo = () => {
    history.push(getUrlByNameAndId(REDIRECT_URLS.QUOTE_VEHICLE_INFO, referenceNumber))
  }

  const redirectDocuments = () => {
    history.push(getUrlByNameAndId(REDIRECT_URLS.QUOTE_DOCUMENT, referenceNumber))
  }

  const redirectWO = (data) => {
    history.replace(getWorkOrderSummaryUrl(data?.reference_number))
  }
  const redirectToForbiddenPage = () => {
    history.replace(REDIRECT_URLS.FORBIDDEN_PAGE)
  }

  const redirectToSupport = () => history.push(REDIRECT_URLS.CREATE_SUPPORT_REQUEST, { referenceNumber })

  const onCloseModal = () => {
    setUpdatedVehicle(null)
    setIsVinEdit(false)
    setIsVinInvalid(false)
  }

  const packagesTotal = packages.length > 0 ? packages.reduce((total, current) => total + current.total_price, 0) : 0

  const servicesTotal = services.length > 0 ? services.reduce((total, current) => total + current.total_price, 0) : 0
  const partsTotal = parts.length > 0 ? parts.reduce((total, current) => total + current.total_price, 0) : 0
  const laborTotal = labor.length > 0 ? labor.reduce((total, current) => total + current.total_price, 0) : 0

  const getCustomItemTotal = useCallback(
    (items = []) =>
      items.length > 0
        ? items.reduce((total_price, current) => {
            if (current.type === 'DISCOUNT') {
              return total_price - current.total_price
            }
            return total_price + current.total_price
          }, 0)
        : 0,
    []
  )

  const customItemTotal = useMemo(() => getCustomItemTotal(customItemList), [customItemList, getCustomItemTotal])
  const rebateCustomItemTotal = useMemo(() => getCustomItemTotal(rebateCustomItemList), [rebateCustomItemList, getCustomItemTotal])

  const totalCost = packagesTotal + servicesTotal + customItemTotal + rebateCustomItemTotal + partsTotal + laborTotal

  const handleConfirmClose = async () => {
    const closeQuoteResponse = closeQuote({
      quoteId: quoteData.id
    })
    closeQuoteResponse
      .then(() => {
        history.push({
          pathname: '/quotes',
          search: `?status=${quoteData.status || 'OPEN'}`
        })
      })
      .catch((error) => console.log(error))
  }
  const handleOpen = () => {
    setOpen(true)
  }

  const onCloseCalibrations = (data) =>
    handleDrawerClose({
      setLoading: () => {}, // or remove if you don't use loading
      setDrawerOpen: setIsCalibrationOpen,
      updateFn: updateWorkOrderCalibrations,
      setItems: setPackages,
      sourceData: quoteData,
      data,
      logFile: 'updateSelectedCalibration',
      itemKey: 'calibrations',
      type: 'quotes',
      onSuccess: fetchData
    })

  const onCloseServices = (data) =>
    handleDrawerClose({
      setLoading: () => {},
      setDrawerOpen: setIsServiceOpen,
      updateFn: updateWorkOrderServices,
      setItems: setServices,
      sourceData: quoteData,
      data,
      logFile: 'updateWorkOrderServices',
      itemKey: 'services',
      type: 'quotes',
      onSuccess: fetchData
    })

  const onCloseParts = (data) =>
    handleDrawerClose({
      setLoading: () => {},
      setDrawerOpen: setIsPartsOpen,
      updateFn: updateWorkOrderParts,
      setItems: setParts,
      sourceData: quoteData,
      data,
      logFile: 'updateWorkOrderParts',
      itemKey: 'parts',
      type: 'quotes',
      onSuccess: fetchData
    })

  const onCloseLabour = (data) =>
    handleDrawerClose({
      setLoading: () => {},
      setDrawerOpen: setIsLabourOpen,
      updateFn: updateWorkOrderLabour,
      setItems: setLabor,
      sourceData: quoteData,
      data,
      logFile: 'updateWorkOrderLabour',
      itemKey: 'labors',
      type: 'quotes',
      onSuccess: fetchData
    })

  const handleClose = () => {
    setOpen(false)
    setCloseInput('')
  }

  const fetchData = async () => {
    try {
      setLoading(true)
      const loadWorkOrderData = async () => {
        const response = await getQuote({
          quoteId: quoteData.id
        })
        if (response?.status !== 200) {
          redirectToForbiddenPage()
          return
        }
        const { data } = response
        const { quote } = data

        // Attach additional data to workOrder
        Object.assign(quote, {
          safetySystemsItems: data.safetySystemsItems,
          calibrations: data.qwiCalibrations,
          services: data.qwiServices,
          parts: data.qwiParts,
          labors: data.qwiLabours,
          customItems: data.qwiCustomItems
        })

        setQuoteData(quote)
        setFormData(quote)
        const { notes_external, notes_internal, client } = quote
        setQuoteInputValues((prev) => ({
          ...prev,
          ...(notes_external && { notes_external }),
          ...(notes_internal && { notes_internal }),
          ...(client?.notes && { client_notes: client.notes })
        }))

        const selectedItemNums = {
          calibrations: [],
          services: [],
          parts: [],
          labours: []
        }

        if (data.qwiCalibrations?.length) {
          const flattened = data.qwiCalibrations.map((item) => flattenItem(item, 'calibration'))
          setPackages(flattened)
          selectedItemNums.calibrations = flattened.map((item) => item.itemNum)
        }

        if (data.qwiServices?.length) {
          const flattened = data.qwiServices.map((item) => flattenItem(item, 'service'))
          setServices(flattened)
          selectedItemNums.services = flattened.map((item) => item.itemNum)
        }

        if (data.qwiParts?.length) {
          const flattened = data.qwiParts.map((item) => flattenItem(item, 'part'))
          setParts(flattened)
          selectedItemNums.parts = flattened.map((item) => item.itemNum)
        }

        if (data.qwiLabours?.length) {
          const flattened = data.qwiLabours.map((item) => flattenItem(item, 'labour'))
          setLabor(flattened)
          selectedItemNums.labours = flattened.map((item) => item.itemNum)
        }

        if (data.qwiCustomItems?.length) {
          const updatedList = normalizeCustomItems(data.qwiCustomItems)
          const { rebateItems, standardItems } = splitCustomItems(updatedList)
          setCustomItemList(standardItems)
          setRebateCustomItemList(rebateItems)
        } else {
          setCustomItemList([])
          setRebateCustomItemList([])
        }

        // await populateSelectableItems(selectedItemNums)
      }
      await loadWorkOrderData()
    } catch (error) {
      Logger.error({
        message: error,
        payload: { ccid: quoteData.calibrationCenter?.id, effectiveDate: quoteData.created_at, quoteId: quoteData.id }
      })
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    if (quoteData?.id) fetchData()
  }, [quoteData?.quoteCreatedAt, quoteData?.calibrationCenter?.id, quoteData?.created_at])

  useEffect(() => {
    const fetchQuoteDataByReferenceNumber = async () => {
      if (!referenceNumber) return

      try {
        setLoading(true)
        const response = await getQuoteByReferenceNumber({ referenceNumber })
        if (response?.status === 200) {
          const data = response.data || {}
          data.quote.safetySystemsItems = data.safetySystemsItems || []
          setQuoteData(data.quote)
          const QWIResponse = await getQWIByQuoteId(data.quote.id)
          setQWIData(QWIResponse?.data)
          if (QWIResponse?.data.invoiceId || QWIResponse?.data.workorderId) {
            history.replace(REDIRECT_URLS.FORBIDDEN_PAGE)
          }
        } else {
          //Given referenceNumber doesnot exist or no permission granted
          redirectToForbiddenPage()
          return
        }
      } catch (error) {
        Logger.error({
          message: error,
          payload: { referenceNumber }
        })
      } finally {
        setLoading(false)
      }
    }

    fetchQuoteDataByReferenceNumber()
  }, [referenceNumber])

  const {
    selectedProduct,
    setSelectedProduct,
    isOpenEditProduct,
    setIsOpenEditProduct,
    handleClickEditPricing,
    handleEditPricingInputChange,
    handleEditPricingSubmit
  } = useEditPricing(fetchData)

  const saveNote = useCallback(
    async (notes) => {
      const isSuccess = (status) => status === 200 || status === 201

      if (notes.noteType === 'client_note') {
        const clientNotes = notes?.note?.client_notes
        const updatedClientData = {
          ...quoteData.client,
          notes: clientNotes
        }

        const { status, data } = await updateClient({ data: updatedClientData })

        if (isSuccess(status)) {
          setQuoteInputValues((prevNotes) => ({
            ...prevNotes,
            client_notes: data.notes
          }))
        }
      } else {
        const { status, data } = await updateNote({
          type: 'quotes',
          id: quoteData.id,
          data: notes
        })

        if (isSuccess(status)) {
          const updatedQuote = data.quote

          setQuoteInputValues((prevNotes) => ({
            ...prevNotes,
            notes_external: updatedQuote.notes_external,
            notes_internal: updatedQuote.notes_internal,
            client_notes: quoteData.client?.notes
          }))
        }
      }
    },
    [quoteData.id, quoteData.client]
  )

  const convertToWoUpdateVin = async () => {
    // eslint-disable-next-line no-unused-vars
    const { safetySystems, color, imageUrl, fuel, ...vehicle } = updatedVehicle
    const submitData = {
      ...formData,
      calibration_center_id: formData.calibrationCenter.id,
      client_id: formData.client.id,
      quote: false,
      insurance_company_id: formData.insurance_company?.id || null,
      vehicle: {
        calibration_center_id: authCtx.user, //change required
        exterior_color: color,
        image_url: imageUrl,
        fuel_capacity_gal: fuel,
        ...vehicle
      },
      safetySystems: updatedVehicle.safetySystems.map((item) => {
        return item.name
      })
    }
    const params = {
      data: submitData,
      quoteId: quoteData.id
    }

    const response = await updateQuote(params)
    if (response.status === 200 || response.status === 201) {
      const converWoResponse = await transformToWO(params)
      if (converWoResponse.status === 200 || converWoResponse.status === 201) {
        redirectWO({ reference_number: submitData.reference_number })
      }
    }
  }

  const convertToWO = async () => {
    const submitData = {
      ...formData,
      calibration_center_id: formData.calibrationCenter.id,
      client_id: formData.client.id,
      quote: false
    }

    const params = {
      data: submitData,
      quoteId: quoteData.id
    }
    if (!formData.vehicle.vin) {
      setIsVinEdit(true)
    } else {
      const response = await transformToWO(params)
      if (response.status === 200 || response.status === 201) {
        redirectWO({ reference_number: submitData.reference_number })
      }
    }
  }

  const updateVehicleInfo = (props) => {
    if (props.data?.make) {
      const updatedVehicle = {
        ...props.data,
        vin: props.vin
      }
      setUpdatedVehicle(updatedVehicle)
      setIsVinInvalid(false)
    } else {
      setUpdatedVehicle(null)
      setIsVinInvalid(true)
    }
  }

  const emailQuoteHandler = useCallback(async () => {
    const params = {
      quoteId: quoteData.id
    }

    setLoading(true)
    try {
      const emailContactResponse = await getQuoteEmails(params)
      if (emailContactResponse.status === 200) {
        setIsEmail(true)
        setEmailContacts(emailContactResponse.data)
      }
      const quotePdfResponse = await getQuotePdf({
        quoteId: quoteData?.id
      })
      if (quotePdfResponse.status === 200) {
        const fileValue = new File([quotePdfResponse.data], `quote_${quoteData?.reference_number}.pdf`)
        fileValue.id = quoteData?.id
        setQuotePdf(fileValue)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { QuoteId: quoteData.id, path: 'QuoteSummaryPage/emailQuoteHandler' } })
    } finally {
      setLoading(false)
    }
  }, [quoteData])

  const vehicleData = quoteData?.vehicle || {}
  const isActive = StatusEnum[quoteData?.status] === StatusEnum.OPEN
  const isServiceOnly = quoteData?.type === 'SERVICE_ONLY'

  const options = [
    {
      label: 'Email Quote',
      action: emailQuoteHandler,
      visible: true
    },
    {
      label: 'Documents',
      action: redirectDocuments,
      visible: true
    },
    {
      label: 'Vehicle Info',
      action: redirectVehicleInfo,
      visible: true
    },
    {
      label: 'Support',
      action: redirectToSupport,
      visible: true
    }
  ]

  const handleClickClosePricing = (type) => {
    setIsOpenEditProduct((prev) => ({ ...prev, [type]: false }))
    setSelectedProduct({})
  }
  const onSaveCustomItem = (items) => {
    const normalizedItems = normalizeCustomItems(items)
    const { rebateItems, standardItems } = splitCustomItems(normalizedItems)
    setCustomItemList(standardItems)
    setRebateCustomItemList(rebateItems)
    fetchData()
  }

  const { populateSelectableItems } = itemHandlersFactory({
    getCalibrationList,
    getServiceList,
    getPartsList,
    getLabourList,
    setAvailableCalibrations,
    setAvailableServices,
    setAvailableParts,
    setAvailableLabour,
    transformToMap
  })
  const drawerSetters = {
    calibrations: setIsCalibrationOpen,
    services: setIsServiceOpen,
    parts: setIsPartsOpen,
    labor: setIsLabourOpen
  }

  const handleManageClick = async (type) => {
    const ccid = quoteData?.calibrationCenter?.id
    const qwi_id = QWIData?.id

    const selected = {
      calibrations: packages,
      services: services,
      parts: parts,
      labours: labor
    }

    let extraParams = {}

    if (type !== 'parts' && referralData?.id) {
      extraParams = {
        belongsTo: 'REFERRAL',
        belongsToId: referralData.id
      }
    }

    await populateSelectableItems(type, ccid, qwi_id, selected, extraParams)
    drawerSetters[type]?.(true)
  }

  useEffect(() => {
    if (QWIData?.referralSourceCode) {
      const fetchReferralData = async () => {
        // Fetch referral data if referralSourceCode is present
        const referral_response = await getReferralByCode({
          ccId: quoteData?.calibrationCenter?.id,
          code: QWIData?.referralSourceCode
        })
        setReferralData(referral_response.data)
      }
      fetchReferralData()
    }
  }, [QWIData?.referralSourceCode, quoteData?.calibrationCenter?.id])

  return (
    <AdasContainer maxWidth='lg'>
      {quoteData.id && (
        <AdasPaper elevation={0} sx={{ padding: '1.25rem', marginTop: '1.25rem', marginBottom: '60px' }}>
          <EmailForm
            quotePdf={quotePdf}
            isOpen={isEmail}
            contacts={emailContacts}
            emailData={quoteData}
            onClose={() => setIsEmail(false)}
          />
          <Drawers data={availableCalibrations} isOpen={isCalibrationOpen} onClose={onCloseCalibrations} title={'Calibrations'} />
          <Drawers data={availableServices} isOpen={isServiceOpen} onClose={onCloseServices} title={'Service'} />
          <Drawers data={availableParts} isOpen={isPartsOpen} onClose={onCloseParts} title={'Parts'} />
          <Drawers data={availableLabour} isOpen={isLabourOpen} onClose={onCloseLabour} title={'Labor'} />

          <AdasBox sx={{ marginBottom: '40px' }}>
            <AdasBox sx={{ overflow: 'hidden' }} display='flex' justifyContent='space-between' flexDirection={{ xs: 'row' }}>
              <AdasBox mb={1}>
                <IdDisplay itemId={quoteData.reference_number} roNumber={quoteData.repair_order_num} itemType={'Quote'} />
                <AdasTypography variant='body2' color={'secondary'} sx={{ display: { xs: 'block', md: 'none' } }}>
                  Created: {new Date(quoteData.created_at).toLocaleDateString()}
                </AdasTypography>
              </AdasBox>
              <AdasBox display='flex' gap={1} alignItems='center' justifyContent='space-between'>
                <AssignedUser type='quotes' workOrderId={quoteData.id} initialAssignee={quoteData.assigned_to} closedStatus={!isActive} />
                <AdasTypography variant='body2' color={'secondary'} sx={{ display: { xs: 'none', md: 'block' } }}>
                  Created: {new Date(quoteData.created_at).toLocaleDateString()}
                </AdasTypography>
              </AdasBox>
            </AdasBox>

            <AdasBox
              style={{
                overflow: 'hidden',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                float: 'right',
                marginTop: '2px'
              }}
            >
              <span style={{ float: 'right', padding: '6px', paddingRight: 0 }}>
                <DropdownActionButtons options={options} />
              </span>
            </AdasBox>
            <AdasBox id='carInfo'>
              {vehicleData?.year && (
                <AdasBox
                  id='image'
                  sx={{
                    marginTop: vehicleData.image_url ? '0px' : '40px'
                  }}
                >
                  {vehicleData.image_url ? <AdasImage src={vehicleData.image_url} alt='Vehicle' /> : <CarImageHolder showText={false} />}
                  <AdasTypography sx={{ ...textDisplay, fontSize: '24px' }}>
                    <b>{`${vehicleData.year} ${vehicleData.make} ${vehicleData.model} ${vehicleData.trim}`}</b>
                  </AdasTypography>
                </AdasBox>
              )}
              <AdasTypography
                sx={{
                  ...textDisplay,
                  fontSize: '20px',
                  fontWeight: '500'
                }}
              >
                <b>Total Cost: {currencyFormatter(totalCost)}</b>
              </AdasTypography>
            </AdasBox>
            <AdasBox id='clientInfo' sx={{ ...textDisplay }}>
              <AdasTypography sx={{ ...textDisplay }} color={theme.typography.secondaryColor}>
                {quoteData.client?.name}
              </AdasTypography>
              {quoteData.client?.rebate_percent !== 0 && quoteData.client?.rebate_percent !== null && (
                <AdasTypography sx={{ ...textDisplay }} color={theme.typography.secondaryColor}>
                  Client Rebate: {quoteData.client?.rebate_percent}%
                </AdasTypography>
              )}
            </AdasBox>
          </AdasBox>
          <AdasBox>
            <AdasStepper orientation='vertical' connector={null} activeStep={-1}>
              <AdasStep key='packages'>
                {QWIData?.referralSourceCode && <ProductContractBanner referralData={referralData} />}
                <AdasPaper variant='outlined' sx={{ backgroundColor: theme.palette.background.light, border: 'none' }}>
                  {!isServiceOnly && (
                    <>
                      <ExpandableSection
                        title='Calibrations'
                        total={packagesTotal}
                        items={packages}
                        manageLabel='MANAGE CALIBRATIONS'
                        onManage={() => handleManageClick('calibrations')}
                        disabled={!isActive}
                        type={'calibration'}
                        onEdit={handleClickEditPricing}
                      />
                      <EditProductPricing
                        key='edit calibration'
                        type='calibration'
                        title='Calibrations'
                        product={selectedProduct}
                        descriptionLength={MAX_DESCRIPTION_LENGTH}
                        isOpen={isOpenEditProduct.calibration}
                        onClose={handleClickClosePricing}
                        handleInputChange={handleEditPricingInputChange}
                        onSubmit={handleEditPricingSubmit}
                        isNational={QWIData?.referralSourceCode}
                      />
                      <AdasDivider orientation='horizontal' flexItem sx={{ margin: '0px 25px' }} />
                    </>
                  )}
                  <ExpandableSection
                    title='Services'
                    total={servicesTotal}
                    items={services}
                    manageLabel='Manage Services'
                    onManage={() => handleManageClick('services')}
                    disabled={!isActive}
                    type={'service'}
                    onEdit={handleClickEditPricing}
                  />
                  <EditProductPricing
                    key='edit service'
                    type='service'
                    title='Services'
                    product={selectedProduct}
                    descriptionLength={MAX_DESCRIPTION_LENGTH}
                    isOpen={isOpenEditProduct.service}
                    onClose={handleClickClosePricing}
                    handleInputChange={handleEditPricingInputChange}
                    onSubmit={handleEditPricingSubmit}
                    isNational={QWIData?.referralSourceCode}
                  />
                  <AdasDivider orientation='horizontal' flexItem sx={{ margin: '0px 25px' }} />
                  <ExpandableSection
                    key='Parts'
                    title='Parts'
                    total={partsTotal}
                    items={parts}
                    manageLabel='Manage Parts'
                    onManage={() => handleManageClick('parts')}
                    type={'part'}
                    onEdit={handleClickEditPricing}
                  />
                  <EditProductPricing
                    key='edit part'
                    type='part'
                    title='Parts'
                    product={selectedProduct}
                    descriptionLength={MAX_DESCRIPTION_LENGTH}
                    isOpen={isOpenEditProduct.part}
                    onClose={handleClickClosePricing}
                    handleInputChange={handleEditPricingInputChange}
                    onSubmit={handleEditPricingSubmit}
                    isNational={QWIData?.referralSourceCode}
                  />
                  <AdasDivider orientation='horizontal' flexItem sx={{ margin: '0px 25px' }} />
                  <ExpandableSection
                    key='Labour'
                    title='Labor'
                    total={laborTotal}
                    items={labor}
                    manageLabel='Manage Labor'
                    onManage={() => handleManageClick('labor')}
                    type={'labor'}
                    onEdit={handleClickEditPricing}
                  />
                  <EditProductPricing
                    key='edit labor'
                    type='labor'
                    title='Labor'
                    product={selectedProduct}
                    descriptionLength={MAX_DESCRIPTION_LENGTH}
                    isOpen={isOpenEditProduct.labor}
                    onClose={handleClickClosePricing}
                    handleInputChange={handleEditPricingInputChange}
                    onSubmit={handleEditPricingSubmit}
                    isNational={QWIData?.referralSourceCode}
                  />
                  <AdasDivider orientation='horizontal' flexItem sx={{ margin: '0px 25px' }} />
                  <CustomItem
                    type='quotes'
                    customItemTotal={customItemTotal}
                    existingCustomItemList={customItemList}
                    workOrderData={quoteData}
                    onSaveCustomItem={onSaveCustomItem}
                    disabled={!isActive}
                  />
                  {rebateCustomItemList.length > 0 && (
                    <CustomItem
                      type='quotes'
                      customItemTotal={rebateCustomItemTotal}
                      existingCustomItemList={rebateCustomItemList}
                      workOrderData={quoteData}
                      onSaveCustomItem={onSaveCustomItem}
                      disabled={!isActive}
                      isCustomItem={false}
                      title='Automatic Client Rebate'
                      showAddButton={false}
                    />
                  )}
                  <AdasDivider orientation='horizontal' flexItem sx={{ margin: '0px 25px' }} />
                  <AdasBox sx={{ display: 'flex', justifyContent: 'space-between', padding: '12px 50px' }}>
                    <AdasTypography sx={{ fontWeight: '600' }}>Total Cost</AdasTypography>
                    <AdasTypography sx={{ fontWeight: '600' }} mr='1.5rem'>
                      {currencyFormatter(totalCost)}
                    </AdasTypography>
                  </AdasBox>
                </AdasPaper>
              </AdasStep>
            </AdasStepper>
            <Note
              existingExternalNote={quoteInputValues.notes_external}
              existingInternalNote={quoteInputValues.notes_internal}
              existingClientNote={quoteInputValues.client_notes}
              submit={(props) => saveNote(props)}
              allowEdit={isActive}
              clientRebate={quoteData.client?.rebate_percent}
            />
          </AdasBox>
          <AdasDivider orientation='horizontal' flexItem />
          <AdasBox sx={{ margin: '20px 0px' }}>
            <AdasBox
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexDirection: { xs: 'column', sm: 'row' }
              }}
            >
              <AdasButton
                sx={{ fontWeight: '600', alignSelf: { xs: 'flex-start', sm: 'center' }, marginBottom: { xs: '1rem', sm: 0 } }}
                onClick={() =>
                  history.push({
                    pathname: '/quotes',
                    search: `?status=${quoteData.status || 'OPEN'}`
                  })
                }
                startIcon={<KeyboardDoubleArrowLeftIcon />}
              >
                Quotes
              </AdasButton>

              <AdasBox
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                  gap: '1rem',
                  width: { xs: '100%', sm: 'auto' },
                  marginTop: { xs: '1rem', sm: 0 }
                }}
              >
                <AdasButton
                  disabled={!isActive}
                  variant='outlined'
                  sx={{ fontWeight: '600' }}
                  onClick={handleOpen}
                  startIcon={<CheckIcon />}
                >
                  CLOSE QUOTE
                </AdasButton>

                <AdasButton
                  variant='contained'
                  startIcon={<ReceiptLongOutlinedIcon />}
                  onClick={convertToWO}
                  disabled={!isActive}
                  isDebounceEnabled={true}
                >
                  CREATE WORK ORDER
                </AdasButton>
              </AdasBox>
            </AdasBox>
          </AdasBox>

          <AdasBox>
            <AdasModal
              open={open}
              onClose={handleClose}
              aria-labelledby='modal-modal-title'
              aria-describedby='modal-modal-description'
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <AdasPaper
                variant='outlined'
                sx={{
                  paddingLeft: '20px',
                  paddingRight: '20px',
                  paddingBottom: '20px'
                }}
              >
                <h4>Close current quote?</h4>
                <p>To confirm that you want to close this quote, type CLOSE below.</p>
                <AdasTextField
                  onChange={(e) => setCloseInput(e.target.value.toUpperCase())}
                  name='closeInput'
                  sx={{ marginBottom: '20px' }}
                  id='closeInput'
                  label='Close Input'
                  variant='outlined'
                  value={closeInput}
                  fullWidth={true}
                  InputLabelProps={{
                    shrink: true
                  }}
                />
                <AdasBox sx={{ display: 'flex' }}>
                  <AdasButton variant='outlined' sx={{ ml: 'auto', mr: '5px' }} onClick={handleClose}>
                    NO
                  </AdasButton>
                  <AdasButton variant='contained' disabled={closeInput === 'CLOSE' ? false : true} onClick={handleConfirmClose}>
                    YES
                  </AdasButton>
                </AdasBox>
              </AdasPaper>
            </AdasModal>
            <AdasModal
              open={isVinEdit}
              onClose={onCloseModal}
              aria-labelledby='modal-modal-title'
              aria-describedby='modal-modal-description'
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <AdasPaper
                variant='outlined'
                sx={{
                  paddingLeft: '20px',
                  paddingRight: '20px',
                  paddingBottom: '20px'
                }}
              >
                <h4>Convert Quote to Work Order</h4>
                <p>A 17 digit VIN is required to create a work order for the vehicle</p>
                <VinInput updateVehicleInfo={updateVehicleInfo} vinExistCheck={true} />
                {updatedVehicle && (
                  <AdasAlert
                    severity='success'
                    // color={theme.palette.background.highlight}
                    sx={{
                      borderRadius: '4px',
                      my: '6px',
                      py: '0px',
                      backgroundColor: theme.palette.background.highlight
                    }}
                  >
                    {`${updatedVehicle.year} ${updatedVehicle.make} ${updatedVehicle.model}`} - Confirmed
                  </AdasAlert>
                )}
                {isVinInvalid && (
                  <AdasAlert
                    severity='error'
                    // color={theme.palette.background.highlight}
                    sx={{
                      borderRadius: '4px',
                      my: '4px'
                    }}
                  >
                    VIN not found!
                  </AdasAlert>
                )}
                <AdasBox
                  sx={{
                    display: 'flex',
                    marginTop: '10px',
                    justifyContent: 'space-between'
                  }}
                >
                  <AdasButton sx={{ marginLeft: 'auto' }} onClick={onCloseModal}>
                    CANCEL
                  </AdasButton>
                  <AdasButton
                    variant='contained'
                    disabled={updatedVehicle && updatedVehicle.vin ? false : true}
                    isDebounceEnabled={true}
                    onClick={convertToWoUpdateVin}
                  >
                    CREATE WORK ORDER
                  </AdasButton>
                </AdasBox>
              </AdasPaper>
            </AdasModal>
          </AdasBox>
        </AdasPaper>
      )}
    </AdasContainer>
  )
}
